/**
 * ReviewPanel — universal read → edit → approve/reject component.
 *
 * Used across all memory approval surfaces (daily memory, reflections,
 * relationship evolution, dream beliefs).  The flow:
 *
 *   1. User sees the proposed content rendered as markdown (read mode)
 *   2. "Edit" toggles a monospace textarea (edit mode)
 *   3. When edited, a diff view highlights changes
 *   4. "Approve" sends the (possibly edited) content
 *   5. "Reject" opens a reason input, then confirms
 *
 * Opt-in per-bullet veto mode (`bulletVeto`): bullet lines in read mode get
 * an ✕ toggle; vetoed bullets are excluded from the approved content and
 * passed to `onApprove` as the second argument, so the caller can record
 * them (the memory surface writes them to the corrections ledger).
 *
 * The component is controlled — parent owns loading/saving state and
 * provides the callbacks.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, Edit3, X, ChevronDown, ChevronUp, Diff, Undo2 } from "lucide-react";
import Markdown from "react-markdown";
import { Button } from "./Button";
import styles from "./ReviewPanel.module.css";

const BULLET_RE = /^\s*[-*•]\s+/;

// ── Inline diff computation ──────────────────────────────────────

interface DiffLine {
	text: string;
	type: "added" | "removed" | "unchanged";
}

function computeLineDiff(original: string, edited: string): DiffLine[] {
	const oldLines = original.split("\n");
	const newLines = edited.split("\n");
	const result: DiffLine[] = [];

	const oldSet = new Set(oldLines.map(l => l.trim()));
	const newSet = new Set(newLines.map(l => l.trim()));

	// Walk through old lines — mark removed ones
	for (const line of oldLines) {
		if (!newSet.has(line.trim())) {
			result.push({ text: line, type: "removed" });
		}
	}
	// Walk through new lines — mark added/unchanged
	for (const line of newLines) {
		if (!oldSet.has(line.trim())) {
			result.push({ text: line, type: "added" });
		} else {
			result.push({ text: line, type: "unchanged" });
		}
	}
	return result;
}

// ── Component ────────────────────────────────────────────────────

export interface ReviewPanelProps {
	/** The proposed content to review (markdown string). */
	content: string;
	/** Panel header title. */
	title: string;
	/** Called with the final content (original or edited). When bulletVeto
	 *  is on and bullets were vetoed, they arrive as the second argument. */
	onApprove: (content: string, vetoedBullets?: string[]) => Promise<void>;
	/** Called with an optional reason. If omitted, reject UI is hidden. */
	onReject?: (reason: string) => Promise<void>;
	/** Whether an action is in flight. */
	loading?: boolean;
	/** Start collapsed (user clicks to expand). */
	defaultCollapsed?: boolean;
	/** Optional icon element to show in header. */
	icon?: React.ReactNode;
	/** Optional transform applied to content before rendering (e.g. wikilink processing). Raw content is always used for editing. */
	processContent?: (content: string) => string;
	/** Keyboard shortcut hints — when true, shows (A)pprove / (R)eject hints on buttons. */
	keyboardHints?: boolean;
	/** Per-bullet veto mode — bullet lines get keep/reject toggles in read mode. */
	bulletVeto?: boolean;
	/** Hint shown while any bullet is vetoed (what vetoing means downstream). */
	vetoHint?: string;
	/** `card` (default): a bordered, collapsible box for pages that list
	 *  several reviews. `flush`: no border, no header, no collapse — the
	 *  document fills its host and the actions pin to the bottom. For a host
	 *  that is already the frame (a dock panel, whose tab carries the title);
	 *  a card inside it is a box in a box. */
	variant?: "card" | "flush";
}

export function ReviewPanel({
	content,
	title,
	onApprove,
	onReject,
	loading = false,
	defaultCollapsed = false,
	icon,
	processContent,
	keyboardHints = false,
	bulletVeto = false,
	vetoHint,
	variant = "card",
}: ReviewPanelProps) {
	const flush = variant === "flush";
	const [collapsed, setCollapsed] = useState(defaultCollapsed);
	const [editing, setEditing] = useState(false);
	const [editedContent, setEditedContent] = useState(content);
	const [rejecting, setRejecting] = useState(false);
	const [rejectReason, setRejectReason] = useState("");
	const [showDiff, setShowDiff] = useState(false);
	const [vetoedIdx, setVetoedIdx] = useState<Set<number>>(new Set());
	const editorRef = useRef<HTMLTextAreaElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	const isEdited = editedContent !== content;
	const contentLines = useMemo(() => content.split("\n"), [content]);

	const diffLines = useMemo(
		() => (isEdited ? computeLineDiff(content, editedContent) : []),
		[content, editedContent, isEdited],
	);

	// Reset state when content prop changes (e.g. navigating to next pending)
	useEffect(() => {
		setEditing(false);
		setEditedContent(content);
		setRejecting(false);
		setRejectReason("");
		setShowDiff(false);
		setVetoedIdx(new Set());
	}, [content]);

	const toggleVeto = useCallback((idx: number) => {
		setVetoedIdx((prev) => {
			const next = new Set(prev);
			if (next.has(idx)) next.delete(idx);
			else next.add(idx);
			return next;
		});
	}, []);

	const handleStartEdit = useCallback(() => {
		setEditing(true);
		setEditedContent(content);
		setRejecting(false);
		setShowDiff(false);
		setTimeout(() => editorRef.current?.focus(), 50);
	}, [content]);

	const handleCancelEdit = useCallback(() => {
		setEditing(false);
		setEditedContent(content);
		setShowDiff(false);
	}, [content]);

	const handleApprove = useCallback(async () => {
		if (!editing && bulletVeto && vetoedIdx.size > 0) {
			const vetoed = contentLines.filter((_, i) => vetoedIdx.has(i)).map((l) => l.trim());
			const kept = contentLines.filter((_, i) => !vetoedIdx.has(i)).join("\n");
			await onApprove(kept, vetoed);
		} else {
			await onApprove(editing ? editedContent : content);
		}
		setEditing(false);
		setShowDiff(false);
		setVetoedIdx(new Set());
	}, [onApprove, editing, editedContent, content, bulletVeto, vetoedIdx, contentLines]);

	const handleRejectConfirm = useCallback(async () => {
		if (onReject) {
			await onReject(rejectReason);
			setRejecting(false);
			setRejectReason("");
		}
	}, [onReject, rejectReason]);

	const handleToggleReject = useCallback(() => {
		if (rejecting) {
			setRejecting(false);
			setRejectReason("");
		} else {
			setRejecting(true);
			setEditing(false);
			setShowDiff(false);
		}
	}, [rejecting]);

	// Keyboard shortcuts — only when panel is focused / not collapsed
	useEffect(() => {
		if (collapsed || !keyboardHints) return;
		const handler = (e: KeyboardEvent) => {
			// Don't capture when typing in an input/textarea
			const tag = (e.target as HTMLElement)?.tagName;
			if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
			// Only capture when this panel (or a child) has focus
			if (!panelRef.current?.contains(e.target as Node)) return;

			if (e.key === "a" && !e.ctrlKey && !e.metaKey && !e.altKey) {
				e.preventDefault();
				handleApprove();
			} else if (e.key === "r" && !e.ctrlKey && !e.metaKey && !e.altKey && onReject) {
				e.preventDefault();
				handleToggleReject();
			}
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [collapsed, keyboardHints, handleApprove, handleToggleReject, onReject]);

	return (
		<div
			className={flush ? styles.panelFlush : styles.panel}
			ref={panelRef}
			tabIndex={-1}
		>
			{/* Header — a flush panel's host already names it (the dock tab),
			    and a flush panel cannot collapse, so the whole row goes. */}
			{!flush && (
			<div className={styles.header}>
				{icon && <span className={styles.headerIcon}>{icon}</span>}
				<span className={styles.headerTitle}>{title}</span>
				{isEdited && editing && (
					<span className={`${styles.headerBadge} ${styles.badgeEdited}`}>Edited</span>
				)}
				{!editing && vetoedIdx.size > 0 && (
					<span className={`${styles.headerBadge} ${styles.badgeVetoed}`}>
						{vetoedIdx.size} rejected
					</span>
				)}
				{!isEdited && !editing && vetoedIdx.size === 0 && (
					<span className={`${styles.headerBadge} ${styles.badgePending}`}>Pending</span>
				)}
				<button
					type="button"
					className={styles.btn}
					onClick={() => setCollapsed(!collapsed)}
					style={{ background: "transparent", border: "none", padding: "2px" }}
				>
					{collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
				</button>
			</div>
			)}

			{(flush || !collapsed) && (
				<>
					{/* Body — read / edit / diff mode */}
					<div className={styles.body}>
						{showDiff && isEdited ? (
							<div className={styles.diffView}>
								{diffLines.map((line, i) => (
									<div key={i} className={`${styles.diffLine} ${styles[`diff_${line.type}`]}`}>
										<span className={styles.diffMarker}>
											{line.type === "added" ? "+" : line.type === "removed" ? "−" : " "}
										</span>
										<span>{line.text || "\u00a0"}</span>
									</div>
								))}
							</div>
						) : editing ? (
							<textarea
								ref={editorRef}
								className={styles.editor}
								value={editedContent}
								onChange={(e) => setEditedContent(e.target.value)}
								disabled={loading}
							/>
						) : bulletVeto ? (
							<div className={styles.vetoList}>
								{contentLines.map((line, i) => {
									if (!BULLET_RE.test(line)) {
										return line.trim() ? (
											<div key={i} className={styles.vetoProse}>
												<Markdown>{processContent ? processContent(line) : line}</Markdown>
											</div>
										) : null;
									}
									const isVetoed = vetoedIdx.has(i);
									return (
										<div key={i} className={`${styles.vetoRow} ${isVetoed ? styles.vetoRowVetoed : ""}`}>
											<button
												type="button"
												className={styles.vetoToggle}
												title={isVetoed ? "Keep this fact" : "Reject this fact — it will be recorded as a correction and never remembered"}
												onClick={() => toggleVeto(i)}
												disabled={loading}
											>
												{isVetoed ? <Undo2 size={12} /> : <X size={12} />}
											</button>
											<span className={styles.vetoText}>{line.replace(BULLET_RE, "")}</span>
										</div>
									);
								})}
								{vetoedIdx.size > 0 && (
									<div className={styles.vetoHint}>
										{vetoHint ?? "Rejected bullets are recorded as corrections and will never be restated."}
									</div>
								)}
							</div>
						) : (
							<div className={styles.markdown}>
								<Markdown>{processContent ? processContent(content) : content}</Markdown>
							</div>
						)}
					</div>

					{/* Reject reason row */}
					{rejecting && (
						<div className={styles.rejectRow}>
							<input
								type="text"
								className={styles.rejectInput}
								placeholder="Reason for rejecting (optional)"
								value={rejectReason}
								onChange={(e) => setRejectReason(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") handleRejectConfirm();
									if (e.key === "Escape") handleToggleReject();
								}}
								autoFocus
							/>
							<button
								type="button"
								className={`${styles.btn} ${styles.btnReject}`}
								onClick={handleRejectConfirm}
								disabled={loading}
							>
								{loading ? "..." : "Confirm reject"}
							</button>
							<button
								type="button"
								className={`${styles.btn} ${styles.btnCancel}`}
								onClick={handleToggleReject}
							>
								Cancel
							</button>
						</div>
					)}

					{/* Actions bar */}
					{!rejecting && (
						<div className={styles.actions}>
							<div className={styles.actionsLeft}>
								{editing ? (
									<>
										<button
											type="button"
											className={`${styles.btn} ${styles.btnCancel}`}
											onClick={handleCancelEdit}
											disabled={loading}
										>
											Cancel edits
										</button>
										{isEdited && (
											<button
												type="button"
												className={`${styles.btn} ${styles.btnDiff}`}
												onClick={() => setShowDiff(!showDiff)}
											>
												<Diff size={13} />
												{showDiff ? "Editor" : "Show diff"}
											</button>
										)}
									</>
								) : (
									<button
										type="button"
										className={`${styles.btn} ${styles.btnEdit}`}
										onClick={handleStartEdit}
										disabled={loading}
									>
										<Edit3 size={13} />
										Edit before approving
									</button>
								)}
							</div>
							<div className={styles.actionsRight}>
								{/* The shared decision pair (Button `decline` + `success`)
								    — the /inbox rows use the same two, so the verbs look
								    the same wherever a proposal is decided. */}
								{onReject && (
									<Button
										size="sm"
										variant="decline"
										icon={<X size={13} />}
										onClick={handleToggleReject}
										disabled={loading}
									>
										Reject{keyboardHints ? " (R)" : ""}
									</Button>
								)}
								<Button
									size="sm"
									variant="success"
									icon={<Check size={13} />}
									onClick={handleApprove}
									disabled={loading}
								>
									{loading
										? "Saving..."
										: isEdited && editing
											? "Approve (edited)"
											: !editing && vetoedIdx.size > 0
												? `Approve (${vetoedIdx.size} rejected)`
												: `Approve${keyboardHints ? " (A)" : ""}`}
								</Button>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
}

export default ReviewPanel;
